"use client";
import React from "react";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import { Description, FieldError, Label } from "../Field";

export interface SliderProps {
  label?: string;
  description?: string;
  tooltip?: string;
  errorMessage?: string;
  size?: Size;
  className?: string;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /** Show the current numeric value to the right of the track */
  showValue?: boolean;
}

export function Slider({
  label,
  description,
  tooltip,
  errorMessage,
  size = "md",
  className,
  value,
  defaultValue = 0,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  showValue = true,
}: SliderProps) {
  const isControlled = typeof value === "number";
  const [internalValue, setInternalValue] = React.useState<number>(defaultValue);
  const currentValue = isControlled ? (value as number) : internalValue;

  const clamped = Math.min(Math.max(currentValue, min), max);
  const range = Math.max(max - min, 0.00001);
  const percent = ((clamped - min) / range) * 100;

  return (
    <div className={composeTailwindRenderProps(className, "group flex w-full flex-col gap-1") as string}>
      {label && (
        <Label size={size} tooltip={tooltip} className="mb-1">
          {label}
        </Label>
      )}

      <div className="flex items-center gap-3">
        <div className="relative w-full select-none">
          {/* Rail */}
          <div className="h-1.5 w-full rounded-full bg-background-muted" />
          {/* Track fill */}
          <div
            className="pointer-events-none absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-action-brand"
            style={{ width: `${percent}%` }}
            aria-hidden
          />
          {/* Range input overlay for interaction */}
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={currentValue}
            disabled={disabled}
            aria-invalid={!!errorMessage}
            onChange={(e) => {
              const next = Number(e.currentTarget.value);
              if (isControlled) {
                onChange?.(next);
              } else {
                setInternalValue(next);
                onChange?.(next);
              }
            }}
            className={twMerge(
              "absolute inset-0 w-full cursor-pointer appearance-none bg-transparent outline-none",
              // WebKit thumb styles
              "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-background-input [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-border-input",
              "hover:[&::-webkit-slider-thumb]:shadow-sm focus-visible:[&::-webkit-slider-thumb]:outline-2 focus-visible:[&::-webkit-slider-thumb]:outline-action-default",
              // Firefox
              "[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-background-input [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-border-input",
              "hover:[&::-moz-range-thumb]:shadow-sm focus-visible:[&::-moz-range-thumb]:outline-2 focus-visible:[&::-moz-range-thumb]:outline-action-default",
              disabled ? "cursor-not-allowed opacity-60" : undefined
            )}
          />
        </div>
        {showValue && (
          <div className="min-w-10 text-right text-sm text-text-heading tabular-nums">{Math.round(clamped)}</div>
        )}
      </div>

      {description && <Description className="mt-2 text-text-muted">{description}</Description>}
      {errorMessage && <FieldError className="mt-1">{errorMessage}</FieldError>}
    </div>
  );
}
